// Malhas do mapa (§9.2.6, §9.2.7; DE-B16 = DE-09): só `data/` e `boot/` fazem fetch (§2.2.2 regra 1), então a busca do
// TopoJSON fica aqui; `state/actions.ts` reexpõe `loadGeo` como ação e `features/map` entrega o JSON ao `GeoStore`, que
// valida (`type`, objetos, `meta.projection`, `registry_id`) e decodifica. Aqui: caminho com hash pelo manifesto
// (§2.7.8), prioridade de rede (§15.7.4), tempo-limite e novas tentativas nas falhas de transporte. Nada de decodificar.
import { STATIC, type StaticKey, type StaticManifest } from './static-manifest';

/** Nomes canônicos das malhas (§9.2.1, §14.10). `zones/{ibge}` é P2 (índice em `zonesIndex`). */
export type GeoName = 'br-uf' | 'br-mun' | 'world' | `zones/${string}`;

export type GeoFailure = 'missing' | 'network' | 'timeout' | 'http' | 'parse' | 'aborted';

export class GeoLoadError extends Error {
  constructor(
    readonly geo: GeoName,
    readonly reason: GeoFailure,
    readonly detail = '',
  ) {
    super(`malha ${geo}: ${reason}${detail ? ` (${detail})` : ''}`);
    this.name = 'GeoLoadError';
  }
  /** Falhas de transporte valem nova tentativa; arquivo ausente do manifesto ou JSON quebrado, não. */
  get retryable(): boolean {
    return this.reason === 'network' || this.reason === 'timeout' || this.reason === 'http';
  }
}

/**
 * §9.2.7: até 3 tentativas nas falhas de rede ou HTTP, com espera de 2 s e 6 s entre elas (a 3ª falha dá o `OopsCard`
 * do `br-uf`, AC-9.2-08; o 18 s da tabela fica para quem chama, como a nova rodada do `br-mun`).
 */
export const GEO_MAX_ATTEMPTS = 3;
export const GEO_RETRY_DELAYS_MS = [2000, 6000, 18000] as const;
const GEO_LAST_DELAY_MS = 18000;
export const GEO_TIMEOUT_MS = 20_000;

/** Prioridade de rede (§9.2.6, §15.7.4): as malhas do Brasil nunca disputam com o herói; o mundo vem de um gesto. */
export function geoPriority(name: GeoName): RequestPriority {
  return name === 'br-uf' || name === 'br-mun' ? 'low' : 'auto';
}

export interface LoadGeoOptions {
  manifest?: StaticManifest;
  fetchImpl?: typeof fetch;
  /** Espera entre tentativas (injetável nos testes); padrão: setTimeout, interrompida pelo signal. */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  timeoutMs?: number;
  /** Tentativas no total (padrão 3). */
  maxAttempts?: number;
}

const KEYS: Record<'br-uf' | 'br-mun' | 'world', StaticKey> = {
  'br-uf': 'geoUf',
  'br-mun': 'geoMun',
  world: 'geoWorld',
};

/** Índice das zonas (P2): `{ "files": { "<ibge>": "/geo/zones/<ibge>.<hash>.topo.json" } }` (formato proposto). */
interface ZonesIndex {
  files: Record<string, string>;
}

function defaultSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('aborted'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error('aborted'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Um pedido com tempo-limite; erros viram `GeoLoadError`. Imutável: `cache: 'default'` (§14.8.2). */
async function fetchOnce(name: GeoName, path: string, o: LoadGeoOptions, signal?: AbortSignal): Promise<unknown> {
  const fetchImpl = o.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const ctrl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, o.timeoutMs ?? GEO_TIMEOUT_MS);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener('abort', onAbort, { once: true });
  try {
    let res: Response;
    try {
      const init: RequestInit = {
        cache: 'default',
        redirect: 'error',
        signal: ctrl.signal,
        priority: geoPriority(name),
      };
      res = await fetchImpl(path, init);
    } catch (e) {
      if (signal?.aborted) throw new GeoLoadError(name, 'aborted');
      throw new GeoLoadError(name, timedOut ? 'timeout' : 'network', (e as Error)?.message ?? '');
    }
    if (!res.ok) throw new GeoLoadError(name, 'http', String(res.status));
    let text: string;
    try {
      text = await res.text();
    } catch (e) {
      if (signal?.aborted) throw new GeoLoadError(name, 'aborted');
      throw new GeoLoadError(name, timedOut ? 'timeout' : 'network', (e as Error)?.message ?? '');
    }
    try {
      return JSON.parse(text) as unknown;
    } catch (e) {
      throw new GeoLoadError(name, 'parse', (e as Error).message);
    }
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
}

async function withRetry(name: GeoName, path: string, o: LoadGeoOptions, signal?: AbortSignal): Promise<unknown> {
  const sleep = o.sleep ?? defaultSleep;
  const attempts = o.maxAttempts ?? GEO_MAX_ATTEMPTS;
  for (let attempt = 1; ; attempt++) {
    try {
      return await fetchOnce(name, path, o, signal);
    } catch (e) {
      const err = e instanceof GeoLoadError ? e : new GeoLoadError(name, 'network', String(e));
      if (!err.retryable || attempt >= attempts) throw err;
      try {
        await sleep(GEO_RETRY_DELAYS_MS[attempt - 1] ?? GEO_LAST_DELAY_MS, signal);
      } catch {
        throw new GeoLoadError(name, 'aborted');
      }
    }
  }
}

/** Caminho servido de uma malha do Brasil ou do mundo (manifesto com hash); `null` = ainda não gerada. */
export function geoPath(name: 'br-uf' | 'br-mun' | 'world', manifest: StaticManifest = STATIC): string | null {
  return manifest[KEYS[name]];
}

/**
 * Busca uma malha e devolve o TopoJSON bruto (o `GeoStore` valida e decodifica). Rejeita só com `GeoLoadError`:
 * `missing` (fora do manifesto), `http`/`network`/`timeout` depois das tentativas, `parse` ou `aborted`.
 */
export async function loadGeo(name: GeoName, signal?: AbortSignal, options: LoadGeoOptions = {}): Promise<unknown> {
  if (signal?.aborted) throw new GeoLoadError(name, 'aborted');
  const manifest = options.manifest ?? STATIC;
  if (name.startsWith('zones/')) {
    const ibge = name.slice('zones/'.length);
    const indexPath = manifest.zonesIndex;
    if (!indexPath) throw new GeoLoadError(name, 'missing', 'sem índice de zonas no manifesto');
    const index = (await withRetry(name, indexPath, options, signal)) as Partial<ZonesIndex> | null;
    const path = index?.files?.[ibge];
    if (!path) throw new GeoLoadError(name, 'missing', `município ${ibge} sem zonas`);
    return withRetry(name, path, options, signal);
  }
  const path = geoPath(name as 'br-uf' | 'br-mun' | 'world', manifest);
  if (!path) throw new GeoLoadError(name, 'missing', 'sem arquivo no manifesto');
  return withRetry(name, path, options, signal);
}
